import { UserButton } from '@clerk/clerk-react';
import { useLocation, Link } from 'react-router-dom';
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  BellIcon
} from '@heroicons/react/24/outline';

// Breadcrumb mapping
const getBreadcrumbs = (pathname) => {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbs = [{ name: 'Home', href: '/' }];

  const routeNames = {
    'users': 'Users',
    'analytics': 'Analytics',
    'errors': 'Errors',
    'database': 'Database',
    'settings': 'Settings'
  };

  paths.forEach((path, index) => {
    const href = '/' + paths.slice(0, index + 1).join('/');
    const name = routeNames[path] || path.charAt(0).toUpperCase() + path.slice(1);
    breadcrumbs.push({ name, href });
  });

  return breadcrumbs;
};

export default function Header({ sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const breadcrumbs = getBreadcrumbs(location.pathname);

  return (
    <header className="sticky top-0 z-30 w-full border-b border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-4 md:px-6 2xl:px-11">
        {/* Left side - Hamburger + Breadcrumbs */}
        <div className="flex items-center gap-4">
          {/* Hamburger menu for mobile */}
          <button
            aria-label="Toggle sidebar"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-600 shadow-sm transition-colors hover:bg-gray-50 lg:hidden"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Breadcrumbs - hidden on mobile */}
          <nav className="hidden md:flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              {breadcrumbs.map((crumb, index) => (
                <li key={crumb.href} className="flex items-center">
                  {index > 0 && (
                    <svg
                      className="mx-2 h-5 w-5 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="text-sm font-medium text-gray-700">
                      {crumb.name}
                    </span>
                  ) : (
                    <Link
                      to={crumb.href}
                      className="text-sm font-medium text-gray-500 transition-colors hover:text-primary"
                    >
                      {crumb.name}
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </nav>

          {/* Page title - shown on mobile instead of breadcrumbs */}
          <h1 className="text-lg font-semibold text-gray-900 md:hidden">
            {breadcrumbs[breadcrumbs.length - 1]?.name || 'Dashboard'}
          </h1>
        </div>

        {/* Right side - Search, Notifications, User */}
        <div className="flex items-center gap-3">
          {/* Search button - will trigger global search modal later */}
          <button
            aria-label="Search"
            className="hidden items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-100 sm:flex"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            <span className="hidden lg:inline">Search...</span>
            <kbd className="hidden rounded bg-white px-2 py-0.5 text-xs font-semibold text-gray-600 shadow lg:inline">
              ⌘K
            </kbd>
          </button>

          {/* Notifications button */}
          <button
            aria-label="Notifications"
            className="relative flex items-center justify-center rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
          >
            <BellIcon className="h-6 w-6" />
            {/* Notification badge - will be dynamic later */}
            <span className="absolute right-1 top-1 flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-danger"></span>
            </span>
          </button>

          {/* Clerk UserButton for profile/sign out */}
          <div className="flex items-center">
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: 'h-9 w-9',
                  userButtonPopoverCard: 'shadow-xl'
                }
              }}
            />
          </div>
        </div>
      </div>
    </header>
  );
}
